import { Link } from "react-router-dom";

import { Pencil, ToggleLeft, ToggleRight, Trash2 } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { formatCurrency } from "~/utils/helpers";

import type { Supplier, SupplierBalanceSummary } from "~/types/purchasing";

interface SuppliersTableProps {
  suppliers: Supplier[];
  isLoading: boolean;
  /** Open AP balance per supplier id (from get_supplier_balances_summary). */
  balances?: Map<string, SupplierBalanceSummary>;
  /** Supplier ids with movements (POs/saldos/precios) — their Eliminar action is disabled. */
  movementSupplierIds?: Set<string>;
  onEdit: (supplier: Supplier) => void;
  onDelete: (supplier: Supplier) => void;
  onToggleActive: (supplier: Supplier) => void;
}

export function SuppliersTable({
  suppliers,
  isLoading,
  balances,
  movementSupplierIds,
  onEdit,
  onDelete,
  onToggleActive,
}: SuppliersTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (suppliers.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        No hay proveedores registrados
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Nombre</TableHead>
          <TableHead>RUT</TableHead>
          <TableHead>Contacto</TableHead>
          <TableHead>Email</TableHead>
          <TableHead className="text-right">Saldo abierto</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead className="text-right">Acciones</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {suppliers.map((supplier, index) => {
          const bal = balances?.get(supplier.id);
          const hasMovements = movementSupplierIds?.has(supplier.id) ?? false;
          return (
            <TableRow
              key={supplier.id}
              className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
              style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
            >
              <TableCell className="font-medium">
                <Link
                  to={`/contabilidad/proveedores/${supplier.id}`}
                  className="text-primary hover:underline"
                >
                  {supplier.name}
                </Link>
                {supplier.is_foreign && (
                  <Badge
                    variant="secondary"
                    className="ml-2 bg-gray-100 text-gray-700"
                  >
                    Extranjero
                  </Badge>
                )}
              </TableCell>
              <TableCell>{supplier.rut || "-"}</TableCell>
              <TableCell>{supplier.contact_name || "-"}</TableCell>
              <TableCell>{supplier.email || "-"}</TableCell>
              <TableCell className="text-right">
                {bal ? (
                  <>
                    <div className="font-medium">
                      ${formatCurrency(bal.saldo_total)}
                    </div>
                    {bal.overdue_amount > 0 && (
                      <div className="text-xs text-red-600">
                        Vencido ${formatCurrency(bal.overdue_amount)}
                      </div>
                    )}
                  </>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>
                <Badge
                  variant={supplier.is_active ? "default" : "secondary"}
                  className={
                    supplier.is_active
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }
                >
                  {supplier.is_active ? "Activo" : "Inactivo"}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onToggleActive(supplier)}
                    title={supplier.is_active ? "Desactivar" : "Activar"}
                  >
                    {supplier.is_active ? (
                      <ToggleRight className="h-4 w-4 text-green-600" />
                    ) : (
                      <ToggleLeft className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(supplier)}
                    title="Editar"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(supplier)}
                    disabled={hasMovements}
                    title={
                      hasMovements
                        ? "Tiene movimientos, no se puede eliminar"
                        : "Eliminar"
                    }
                  >
                    <Trash2
                      className={`h-4 w-4 ${hasMovements ? "text-muted-foreground" : "text-red-500"}`}
                    />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
